import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { APP_CONFIG, NAV_ITEMS } from '../config/constants';
import DownloadModal from './DownloadModal';

const Nav = styled.nav`
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
    flex-wrap: nowrap;
  }
`;

const MobileRightSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
    margin-left: 0.75rem;
  }
`;

const NavCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 768px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    transform: none;
    background: rgba(13, 13, 38, 0.95);
    backdrop-filter: blur(30px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding: 1rem;
    display: ${props => props.$isOpen ? 'block' : 'none'};
    z-index: 99;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  transition: all 0.3s ease;
  flex-shrink: 1;
  min-width: 0;
  
  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    gap: 0.5rem;
    flex-shrink: 1;
    min-width: 0;
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const LogoIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const LogoText = styled.span`
  @media (max-width: 768px) {
    word-wrap: break-word;
    word-break: break-word;
    line-height: 1.2;
  }
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
`;

const NavItem = styled.li``;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.$active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: 500;
  transition: all 0.3s;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};

  &:hover {
    color: rgba(255, 255, 255, 0.95);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const DownloadButton = styled.button`
  background: linear-gradient(135deg, #7359B3 0%, #593399 100%);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.5;
  height: 44px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileDownloadButton = styled.button`
  background: linear-gradient(135deg, #7359B3 0%, #593399 100%);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  font-size: 1rem;
  text-decoration: none;
  display: none;
  align-items: center;
  justify-content: center;
  line-height: 1.5;
  height: 44px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    display: inline-flex;
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
    height: 36px;
    border-radius: 12px;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  flex-direction: column;
  gap: 4px;
  transition: all 0.3s;

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const HamburgerLine = styled.span`
  width: 24px;
  height: 2px;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s;
  transform-origin: center;

  ${props => props.$isOpen && `
    &:nth-child(1) {
      transform: rotate(45deg) translate(5px, 5px);
    }
    &:nth-child(2) {
      opacity: 0;
    }
    &:nth-child(3) {
      transform: rotate(-45deg) translate(7px, -6px);
    }
  `}
`;

const Header = () => {
  const location = useLocation();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <Nav>
        <NavContainer>
          <Logo to="/">
            <LogoIcon src="/logo.png" alt={`${APP_CONFIG.name} logo`} />
            <LogoText>{APP_CONFIG.name}</LogoText>
          </Logo>
          
          <DownloadButton onClick={() => setIsDownloadModalOpen(true)}>
            Download App
          </DownloadButton>
          
          <MobileRightSection>
            <MobileDownloadButton onClick={() => setIsDownloadModalOpen(true)}>
              Download App
            </MobileDownloadButton>
            <HamburgerButton onClick={toggleMobileMenu}>
              <HamburgerLine $isOpen={isMobileMenuOpen} />
              <HamburgerLine $isOpen={isMobileMenuOpen} />
              <HamburgerLine $isOpen={isMobileMenuOpen} />
            </HamburgerButton>
          </MobileRightSection>
          
          <NavCenter $isOpen={isMobileMenuOpen}>
            <NavList>
              {NAV_ITEMS.map((item) => (
                <NavItem key={item.path}>
                  <NavLink 
                    to={item.path} 
                    $active={location.pathname === item.path}
                    onClick={closeMobileMenu}
                  >
                    {item.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavList>
          </NavCenter>
        </NavContainer>
      </Nav>
      
      <DownloadModal 
        isOpen={isDownloadModalOpen} 
        onClose={() => setIsDownloadModalOpen(false)} 
      />
    </>
  );
};

export default Header;
